import { Link } from 'react-router-dom';
import { ArrowLeft, Type, Volume2, Monitor } from 'lucide-react';
import { useAccessibility } from '@/contexts/AccessibilityContext';

/**
 * Accessibility settings page — surfaces 3 toggles designed for elderly
 * patients: large text mode, voice guidance during exercises, and keep-
 * screen-awake during a session.
 */
const SettingRow = ({ icon: Icon, title, description, checked, onChange, testId }) => (
  <div className="flex items-start justify-between gap-4 py-5 border-b border-slate-200 last:border-b-0">
    <div className="flex items-start gap-3 flex-1 min-w-0">
      <div className="shrink-0 w-10 h-10 rounded-xl bg-cyan-50 border border-cyan-100 flex items-center justify-center text-cyan-700">
        <Icon size={20} strokeWidth={2.2} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-slate-900 text-base">{title}</p>
        <p className="text-sm text-slate-600 mt-1 leading-relaxed">{description}</p>
      </div>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      className={`relative shrink-0 w-14 h-8 rounded-full transition-colors duration-200 ${
        checked ? 'bg-cyan-500' : 'bg-slate-300'
      }`}
    >
      <span
        className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform duration-200 ${
          checked ? 'translate-x-6' : 'translate-x-0'
        }`}
      />
    </button>
  </div>
);

const AccessibilitySettingsPage = () => {
  const {
    largeText, setLargeText,
    voiceGuidance, setVoiceGuidance,
    screenAwake, setScreenAwake,
  } = useAccessibility();

  const wakeLockSupported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;
  const ttsSupported = typeof window !== 'undefined' && !!window.speechSynthesis;

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-slate-50 py-10 px-4">
      <div className="max-w-2xl mx-auto">
        <Link
          to="/mon-espace"
          className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-6"
          data-testid="accessibility-back-link"
        >
          <ArrowLeft size={14} /> Retour à Mon espace
        </Link>

        <h1 className="text-3xl font-bold text-slate-900 mb-2">Accessibilité</h1>
        <p className="text-slate-600 mb-8">
          Ajustez l'application pour un confort visuel et sonore adapté.
        </p>

        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 px-6">
          <SettingRow
            icon={Type}
            title="Gros caractères"
            description="Augmente la taille du texte sur toute l'application (×1.5)."
            checked={largeText}
            onChange={setLargeText}
            testId="toggle-large-text"
          />
          <SettingRow
            icon={Volume2}
            title="Guidage vocal des exercices"
            description={ttsSupported
              ? 'Une voix annonce les phases « Inspirez », « Expirez » et « Retenez » pendant les exercices guidés.'
              : 'Fonction non disponible sur ce navigateur.'}
            checked={ttsSupported && voiceGuidance}
            onChange={ttsSupported ? setVoiceGuidance : () => {}}
            testId="toggle-voice-guidance"
          />
          <SettingRow
            icon={Monitor}
            title="Garder l'écran allumé"
            description={wakeLockSupported
              ? "L'écran reste allumé pendant tout l'exercice, pour que vous n'ayez pas à y toucher."
              : "Non supporté sur ce navigateur — gardez l'écran allumé manuellement si besoin."}
            checked={wakeLockSupported && screenAwake}
            onChange={wakeLockSupported ? setScreenAwake : () => {}}
            testId="toggle-screen-awake"
          />
        </div>

        <p className="text-xs text-slate-500 mt-6 text-center">
          Ces préférences sont enregistrées localement sur votre appareil.
        </p>
      </div>
    </div>
  );
};

export default AccessibilitySettingsPage;
